import { graphqlAdminRequest, executeAdminMutation } from "./graphqlAdminRequest";
import { getAccessToken, getApiToken } from './authService';

// Helper to convert 'YYYY-MM-DD' to ISO 8601 string (UTC midnight)
function toISODate(dateStr) {
  if (!dateStr) return undefined;
  // If already in ISO format with time, return as is
  if (dateStr.length > 10) return dateStr;
  return new Date(dateStr + 'T00:00:00Z').toISOString();
}

// Helper to convert ISO string to 'YYYY-MM-DD'
function toDateInputValue(isoString) {
  if (!isoString) return '';
  return isoString.slice(0, 10);
}

// List customers
export async function getCustomers(msalInstance, account) {
  const accessToken = await getAccessToken(msalInstance, account);
  const query = `query Customers {
  customers {
    id
    name
    industry
    subscription
    subscriptionStartDate
    subscriptionEndDate
    _createdTs
    _updatedTs
    _updatedBy
    _ts
  }
}`;
const response = await graphqlAdminRequest(query, {}, accessToken);
const customers = response.customers;
console.log('Fetched customers:', customers);
return customers;
}

// Get subscription options for form
export async function getSubscriptionOptions(msalInstance, account) {
  const accessToken = await getApiToken(msalInstance, account);
  const query = `query SubscriptionOptions {
    subscriptions {
      id
      name
      description
      _createdTs
      _updatedTs
    }
  }`;
  const response = await graphqlAdminRequest(query, {}, accessToken);
  return response.subscriptions;
}

// Get customer by ID
export async function getCustomer(msalInstance, account, id) {
  const accessToken = await getApiToken(msalInstance, account);
  const query = `query ($id: ID!) { customer(id: $id) { id name industry subscription subscriptionStartDate subscriptionEndDate _createdTs _updatedTs _updatedBy _ts } }`;
  const customer = (await graphqlAdminRequest(query, { id }, accessToken)).customer;
  // Convert ISO dates to 'YYYY-MM-DD' for form fields
  return {
    ...customer,
    subscriptionStartDate: toDateInputValue(customer.subscriptionStartDate),
    subscriptionEndDate: toDateInputValue(customer.subscriptionEndDate),
  };
}

// Create customer
export async function createCustomer(msalInstance, account, input) {
  const accessToken = await getApiToken(msalInstance, account);
  const mutation = `
    mutation (
      $name: String!,
      $industry: String!,
      $startDate: DateTime,
      $endDate: DateTime,
      $notes: String,
      $dataAccess: [ProductAccessInput!]!,
      $outlooks: [OutlookAccessInput!]!,
      $publicationAccess: [String!]
    ) {
      createCustomer(
        name: $name,
        industry: $industry,
        startDate: $startDate,
        endDate: $endDate,
        notes: $notes,
        dataAccess: $dataAccess,
        outlooks: $outlooks,
        publicationAccess: $publicationAccess,
      ) {
        id
        name
        industry
        startDate
        endDate
        notes
        dataAccess { product activities { activity } regions { region } }
        outlooks { key label }
        publicationAccess
        _createdTs
        _updatedTs
        _updatedBy
        _ts
      }
    }
  `;
  const variables = {
    name:       input.name,
    industry:   input.industry,
    startDate:  toISODate(input.startDate),
    endDate:    toISODate(input.endDate),
    notes:      input.notes,
    dataAccess: input.dataAccess,
    outlooks:   input.outlooks,
    publicationAccess: input.publicationAccess,
  };
  return (await executeAdminMutation(mutation, variables, accessToken)).createCustomer;
}

// Update customer
export async function updateCustomer(msalInstance, account, id, data) {
  const accessToken = await getApiToken(msalInstance, account);

  const variables = {
    id,
    name:       data.name,
    industry:   data.industry,
    startDate:  toISODate(data.startDate),
    endDate:    toISODate(data.endDate),
    notes:      data.notes,
    dataAccess: data.dataAccess,
    outlooks:   data.outlooks,
    publicationAccess: data.publicationAccess,
  };

  const mutation = `
    mutation (
      $id: ID!,
      $name: String,
      $industry: String,
      $startDate: DateTime,
      $endDate: DateTime,
      $notes: String,
      $dataAccess: [ProductAccessInput!],
      $outlooks: [OutlookAccessInput!]!,
      $publicationAccess: [String!]
    ) {
      updateCustomer(
        id: $id,
        name: $name,
        industry: $industry,
        startDate: $startDate,
        endDate: $endDate,
        notes: $notes,
        dataAccess: $dataAccess,
        outlooks: $outlooks,
        publicationAccess: $publicationAccess,
      ) {
        id
        name
        industry
                startDate
        endDate
        notes
        dataAccess { product activities { activity } regions { region } }
        outlooks { key label }
        publicationAccess
        _createdTs
        _updatedTs
        _updatedBy
        _ts
      }
    }
  `;
  return (await executeAdminMutation(mutation, variables, accessToken)).updateCustomer;
}

// Delete customer
export async function deleteCustomer(msalInstance, account, id) {
  const accessToken = await getApiToken(msalInstance, account);
  const mutation = `mutation ($deleteCustomerId: ID!) {
  deleteCustomer(id: $deleteCustomerId)
}`;
  const response = await executeAdminMutation(mutation, { deleteCustomerId: id }, accessToken);
  const message = response.deleteCustomer;
  return response;
}
